import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { LuDatabase, LuFileText, LuFilter, LuPlus } from "react-icons/lu";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import NoteForm from "../../components/payments/NoteForm";
import PaymentForm from "../../components/payments/PaymentForm";
import PaymentList from "../../components/payments/PaymentList";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  actionBarEndClass,
  buttonSpinnerClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
  summaryCardTitleClass,
  summaryCardValueClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPaymentsListData,
  getIsCreateModalOpen,
  getParty,
  getPayments,
  getTotalAmount,
  getTotalInvoice,
  getTotalNoteAmount,
  getTotalPayments,
  getTotalPaymnet,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { DELETE, GET, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "પાર્ટીઓ", href: route.parties },
  { label: "પાર્ટી એકાઉન્ટ" },
];

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

interface FilterFormInput {
  fromDate: string;
  toDate: string;
}

const emptyFilters: FilterFormInput = {
  fromDate: "",
  toDate: "",
};

const PaymentsPage = () => {
  const { id } = useParams();
  const listKey = `parties:payments-${id}`;
  const dispatch = useAppDispatch();

  const isCreateModalOpen = useAppSelector(getIsCreateModalOpen);
  const payments = useAppSelector(getPayments);
  const totalPayments = useAppSelector(getTotalPayments);
  const party = useAppSelector(getParty);
  const totalInvoice = useAppSelector(getTotalInvoice);
  const totalPaymnet = useAppSelector(getTotalPaymnet);
  const totalNoteAmount = useAppSelector(getTotalNoteAmount);
  const totalAmount = useAppSelector(getTotalAmount);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.paymentsCreate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.paymentsReport
  );

  const { handleSubmit, register, reset, getValues } =
    useForm<FilterFormInput>({ defaultValues: savedList.filters });

  const {
    handleSubmit: handleSubmitReport,
    register: registerReport,
    reset: resetReport,
    getValues: getValuesReport,
  } = useForm<FilterFormInput>({ defaultValues: savedList.reportFilters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [showExport, setShowExport] = useState<boolean>(
    !!savedList.showExport
  );

  const fetchId = useRef(0);

  const summaryCards = [
    { label: "ટોટલ હિસાબ", value: totalInvoice },
    { label: "ટોટલ આપેલ પેમેન્ટ", value: totalPaymnet },
    { label: "ટોટલ વધ ઘટ", value: totalNoteAmount },
    { label: "બાકી રહેતું પેમેન્ટ", value: totalAmount },
  ];

  const partyLabel = () => {
    const parts = [party?.firm_name, party?.name].filter(Boolean).join(" - ");
    return party?.phone ? `${parts} (${party.phone})` : parts;
  };

  const handleOnFilterSubmit = async () => {
    if (page === 1) {
      fetchPaymentList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    if (page === 1) {
      fetchPaymentList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleShowCreateForm = async (type: number) => {
    dispatch(toggleCreateModal(type));
  };

  const handleSavePayment = async (data: any) => {
    try {
      if (data && data.id) {
        await PUT(`/v1/admin/payments/${data.id}`, { ...data })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchPaymentList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/payments", { ...data })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchPaymentList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeletePayment = async (id: number) => {
    try {
      await DELETE(`/v1/admin/payments/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = payments.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchPaymentList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleClearReportFilter = () => {
    resetReport(emptyFilters);
    dispatch(
      saveListState({ key: listKey, value: { reportFilters: emptyFilters } })
    );
  };

  const handleExportReport = async () => {
    try {
      setIsReportLoading(true);
      const data = getValuesReport();

      dispatch(
        saveListState({ key: listKey, value: { reportFilters: data } })
      );

      await GET(`/v1/admin/payments/parties/${id}/reports`, {
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
      })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            dispatch(updatePdfFile(response.data.data));
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsReportLoading(false);
    }
  };

  const fetchPaymentList = async (page: number, size: number) => {
    const data = getValues();
    const currentFetch = ++fetchId.current;

    dispatch(saveListState({ key: listKey, value: { filters: data } }));
    setIsLoading(true);

    dispatch(
      fetchPaymentsListData({
        id: id || "0",
        queryParams: {
          page: page,
          size: size,
          fromDate: data?.fromDate || null,
          toDate: data?.toDate || null,
        },
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({
        key: listKey,
        value: {
          page,
          pageSize,
          showFilter,
          showExport,
        },
      })
    );
  }, [dispatch, listKey, page, pageSize, showFilter, showExport]);

  useEffect(() => {
    fetchPaymentList(page, pageSize);
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>પાર્ટી એકાઉન્ટ વિગતો</h1>

        {party && party.id && (
          <Link
            to={route.partyDetail.replace(":id", party.id as unknown as string)}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline text-primary"
          >
            {partyLabel()}
          </Link>
        )}
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarEndClass}>
          <div
            className={cx(
              "grid w-full gap-2 sm:flex sm:w-auto",
              canCreate || canReport ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            <Button
              variant="outline"
              onClick={() => setShowFilter(!showFilter)}
            >
              <LuFilter className="h-4 w-4" />
              {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
            </Button>

            {canReport && (
              <Button
                variant="outline"
                onClick={() => setShowExport(!showExport)}
              >
                <LuFileText className="h-4 w-4" />
                એકાઉન્ટ રિપોર્ટ
              </Button>
            )}

            {canCreate && (
              <Button variant="primary" onClick={() => handleShowCreateForm(2)}>
                <LuPlus className="h-4 w-4" />
                પેમેન્ટ
              </Button>
            )}

            {canCreate && (
              <Button variant="primary" onClick={() => handleShowCreateForm(3)}>
                <LuPlus className="h-4 w-4" />
                વધ ઘટ
              </Button>
            )}
          </div>
        </div>

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={cx(cardTitleClass, "text-lg")}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showExport && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={cx(cardTitleClass, "text-lg")}>
                એકાઉન્ટ રિપોર્ટ
              </div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmitReport(handleExportReport)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...registerReport("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...registerReport("toDate")}
                      />
                    </div>
                  </div>
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearReportFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                    disabled={isReportLoading}
                  >
                    {isReportLoading && <span className={buttonSpinnerClass} />}
                    રિપોર્ટ બનાવો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {summaryCards.map((card) => (
            <div key={card.label} className={cardClass}>
              <div className={cx(cardHeaderClass, "pb-2")}>
                <div className={summaryCardTitleClass}>{card.label}</div>
              </div>

              <div className={cardContentClass}>
                <div className={summaryCardValueClass}>
                  {formatAmount(card.value)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {isLoading && !party?.id ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : (
          <PaymentList
            payments={payments}
            pageNumber={page}
            pageSize={pageSize}
            isLoading={isLoading}
            handleDeletePayment={handleDeletePayment}
          />
        )}

        {!isLoading && payments.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalPayments}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="પાર્ટી એકાઉન્ટ"
          />
        )}



        {isCreateModalOpen === 2 && (
          <PaymentForm handleSavePayment={handleSavePayment} />
        )}

        {isCreateModalOpen === 3 && (
          <NoteForm handleSavePayment={handleSavePayment} />
        )}
      </div>
    </AppLayout>
  );
};
export default PaymentsPage;
